import type { Tournament, TournamentSessionRequest } from '@brush/api-client';
import { Field, FormSection, modalInput } from '../Modal';
import { TagInput } from '../TagInput';
import { SessionEditor } from './SessionEditor';

/**
 * The editable shape of a tournament, shared by the create and edit dialogs.
 *
 * One draft type rather than two: the dialogs render the same fields in the same order, and
 * holding a `CreateTournamentRequest` in one and a patch-shaped object in the other is how they
 * quietly stopped agreeing about which fields existed. Each dialog maps this to its own request
 * on save, which is the only part that genuinely differs.
 *
 * Optional values are held as concrete strings — blank rather than null — because an input
 * always binds to a string. Turning blank back into "none" is the mapping's job.
 */
export interface TournamentDraft {
  internalId: string;
  name: string;
  date: string;
  time: string;
  buyIn: number;
  /** Null is a real state: nothing was guaranteed, which is not a guarantee of zero. */
  guarantee: number | null;
  guaranteeUnits: string;
  eventNumber: string;
  isMultiDay: boolean;
  sessions: TournamentSessionRequest[];
  status: string;
  tags: string[];
}

/**
 * A new tournament, dated the room's own day — its gaming day from the server (ADR 0014), never the
 * browser's UTC date, which is tomorrow every evening west of Greenwich.
 */
export const newDraft = (defaultTags: string[], today: string): TournamentDraft => ({
  internalId: '',
  name: '',
  date: today,
  time: '12:00',
  buyIn: 500,
  guarantee: null,
  guaranteeUnits: '',
  eventNumber: '',
  isMultiDay: false,
  sessions: [],
  status: 'scheduled',
  tags: defaultTags,
});

export const draftFrom = (t: Tournament): TournamentDraft => ({
  internalId: t.internalId,
  name: t.name,
  date: t.date,
  time: t.time,
  buyIn: t.buyIn,
  guarantee: t.guarantee ?? null,
  guaranteeUnits: t.guaranteeUnits ?? '',
  eventNumber: t.eventNumber ?? '',
  isMultiDay: t.isMultiDay,
  sessions: t.sessions.map((s) => ({
    day: s.day,
    sessionName: s.sessionName,
    date: s.date,
    time: s.time,
    name: s.name,
  })),
  status: t.status,
  tags: t.tags,
});

/** Mirrors what the server enforces, so the ordinary case never round-trips to find out. */
export const draftIsValid = (draft: TournamentDraft): boolean =>
  draft.name.trim().length > 0 &&
  draft.internalId.trim().length > 0 &&
  draft.date !== '' &&
  draft.time !== '' &&
  draft.buyIn >= 0;

export function TournamentFields({
  draft,
  onChange,
  allTags,
  platformTags,
  defaultTagsNote,
  showStatus,
  sessionsRemovedWarning,
}: {
  draft: TournamentDraft;
  onChange: (draft: TournamentDraft) => void;
  allTags: string[];
  platformTags: string[];
  /** Shown under the tag input when the room pre-filled them. Create only. */
  defaultTagsNote?: boolean;
  /** Initial status is a creation choice; afterwards the list and detail view drive it. */
  showStatus?: boolean;
  /** Set when unticking multi-day would discard sittings already saved on the server. */
  sessionsRemovedWarning?: number;
}) {
  const set = <K extends keyof TournamentDraft>(key: K, value: TournamentDraft[K]) =>
    onChange({ ...draft, [key]: value });

  // Unticking keeps nothing: the server refuses sessions on a single-day tournament, and holding
  // them here to reappear on a later tick would be a state the form remembers and the record
  // does not.
  const setMultiDay = (on: boolean) =>
    onChange({ ...draft, isMultiDay: on, sessions: on ? draft.sessions : [] });

  return (
    <div className="space-y-6">
      <FormSection title="The event">
        <Field label="Tournament name">
          <input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. $500 Daily Deep Stack"
            className={modalInput}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Internal ID" hint="Your own reference. Unique within this room.">
            <input
              value={draft.internalId}
              onChange={(e) => set('internalId', e.target.value)}
              placeholder="e.g. E-101"
              className={modalInput}
            />
          </Field>
          <Field label="Event number" hint="Optional — as printed on the series schedule.">
            <input
              value={draft.eventNumber}
              onChange={(e) => set('eventNumber', e.target.value)}
              placeholder="e.g. 1A"
              maxLength={10}
              className={modalInput}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Schedule">
        {/* One column until there is room for two: a native date input and a native time input
            both have a min-content width around 150px, so side by side on a phone they clip
            rather than shrink. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Date">
            <input
              type="date"
              value={draft.date}
              onChange={(e) => set('date', e.target.value)}
              className={modalInput}
            />
          </Field>
          <Field label="Start time">
            <input
              type="time"
              value={draft.time}
              onChange={(e) => set('time', e.target.value)}
              className={modalInput}
            />
          </Field>
        </div>

        <div className="border-t border-dashed border-ink-800/10 pt-3">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={draft.isMultiDay}
              onChange={(e) => setMultiDay(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-ink-800">Runs over multiple sessions</span>
          </label>

          {draft.isMultiDay ? (
            <div className="mt-3">
              <SessionEditor
                sessions={draft.sessions}
                parentName={draft.name}
                parentDate={draft.date}
                parentTime={draft.time}
                onChange={(sessions) => set('sessions', sessions)}
              />
            </div>
          ) : sessionsRemovedWarning ? (
            <p className="mt-2 text-xs text-chip-500">
              Saving now removes this tournament&apos;s {sessionsRemovedWarning} saved{' '}
              {sessionsRemovedWarning === 1 ? 'session' : 'sessions'}.
            </p>
          ) : null}
        </div>
      </FormSection>

      <FormSection title="Buy-in and guarantee">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Buy-in ($)">
            <input
              type="number"
              min={0}
              value={draft.buyIn}
              onChange={(e) => set('buyIn', Number(e.target.value))}
              className={modalInput}
            />
          </Field>
          {/* Empty stays null rather than becoming 0 — "nothing was guaranteed" and "we
              guaranteed zero" are different claims, and the record keeps them apart. */}
          <Field label="Guarantee" hint="Blank for none.">
            <input
              type="number"
              min={0}
              value={draft.guarantee ?? ''}
              onChange={(e) =>
                set('guarantee', e.target.value === '' ? null : Number(e.target.value))
              }
              placeholder="Optional"
              className={modalInput}
            />
          </Field>
          <Field label="Counted in" hint="Blank for dollars.">
            <input
              value={draft.guaranteeUnits}
              onChange={(e) => set('guaranteeUnits', e.target.value)}
              placeholder="e.g. seats"
              maxLength={120}
              className={modalInput}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Tags" hint="Used when building leaderboards and ratings">
        {showStatus ? (
          <Field label="Initial status">
            <select
              value={draft.status}
              onChange={(e) => set('status', e.target.value)}
              className={modalInput}
            >
              <option value="scheduled">Scheduled</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
            </select>
          </Field>
        ) : null}

        <div className="min-w-0">
          <TagInput
            value={draft.tags}
            onChange={(tags) => set('tags', tags)}
            suggestions={allTags}
            platformTags={platformTags}
          />
          <p className="mt-1 text-[10px] text-ink-800/40">
            {defaultTagsNote
              ? "Pre-filled with your room's default tags. Change these in Settings."
              : "Platform tags carry a shield and can't be removed by rooms once applied."}
          </p>
        </div>
      </FormSection>
    </div>
  );
}
